import { useCountdown } from '../hooks/useCountdown';
import React from 'react';
import type { Duration } from 'date-fns';
import add from 'date-fns/add';

export type StageKind = 1 | 2 | 3;

export type StageData = {
  title: string;
  number: StageKind;
};

export type StageLeaf = StageData;

export type StageNode = StageData & {
  duration: Duration;
  // modeling the relationship between stages recursively
  // to not rely on implicit array indexes for order
  nextStage: Stage;
};

export type BaseStage = StageNode & {
  startDate: number;
};

// A stage can either contain just its own data
// or its data + information about the next stage
export type Stage = StageLeaf | StageNode;

const stageDuration = {
  days: 14,
} as const;

export const eggStages: BaseStage = {
  title: 'Stage 1',
  // 8AM PST = 4PM UTC
  startDate: Date.UTC(2022, 7, 22, 15),
  duration: stageDuration,
  number: 1,
  nextStage: {
    title: 'Stage 2',
    number: 2,
    duration: stageDuration,
    nextStage: {
      title: 'Stage 3',
      number: 3,
    },
  },
} as const;

type EggCountdownContextType =
  | { type: 'countdown'; stage: StageNode; countdown: Duration }
  // A countdown can only exist within the context of an upcoming stage
  | { type: 'lastStage'; stage: StageLeaf };

export const EggStageContext = React.createContext<Stage>(eggStages);
export const EggCountdownContext = React.createContext<EggCountdownContextType | undefined>(
  undefined,
);

export function findCurrentStage(
  stage: Stage,
  startDate: number,
): { stage: Stage; nextStageStart?: number } {
  if (!('nextStage' in stage)) {
    return { stage };
  }

  const now = new Date().getTime();
  const nextStartDate = add(startDate, stage.duration).getTime();

  if (now > startDate && now < nextStartDate) {
    return { stage, nextStageStart: nextStartDate };
  }

  return findCurrentStage(stage.nextStage, nextStartDate);
}

export function EggCountdownProvider({ children }: React.PropsWithChildren<{}>) {
  const { stage, nextStageStart } = findCurrentStage(eggStages, eggStages.startDate);
  const isNode = 'nextStage' in stage;
  const countdown = useCountdown(nextStageStart);

  return (
    <EggCountdownContext.Provider
      value={isNode ? { type: 'countdown', countdown, stage } : { type: 'lastStage', stage }}
    >
      {children}
    </EggCountdownContext.Provider>
  );
}

export function EggStageProvider({ children }: React.PropsWithChildren<{}>) {
  const { stage } = findCurrentStage(eggStages, eggStages.startDate);
  // we need to hook into the countdown in here in order to sync the update of
  // stages with the countdown without changing the context value and causing
  // re-render on dependent components
  useEggCountdownContext();

  return <EggStageContext.Provider value={stage}>{children}</EggStageContext.Provider>;
}

export function useEggStageContext(): Stage {
  return React.useContext(EggStageContext);
}

export function useEggCountdownContext(): EggCountdownContextType {
  const context = React.useContext(EggCountdownContext);
  if (!context) {
    throw Error('Missing Egg Countdown Context');
  }
  return context;
}
